/* algolia-insights-add-to-cart.js
   For the Add-to-Cart page ONLY.
   Assumes search-insights is already loaded via a <script src="...search-insights.min.js"></script> in the Razor template.
*/

// Helper to strip query/hash from URLs for structured data
function cleanUrl(raw) {
    try { const u = new URL(raw, window.location.origin); u.search = ''; u.hash = ''; return u.toString(); } catch { return raw.split('?')[0]; }
}

/* -------------------------------------------
   Hostname -> index mapping
-------------------------------------------- */
function resolveIndexFromHost() {
    const host = (window.location.hostname || "").toLowerCase();

    // NOTE: adjust if you have "www." or other variants
    if (host.includes("products.artcraftpromos.com")) return "apc_cobrand_base";
    if (host.includes("savingsource.com")) return "svs_cobrand_base";
    if (host.includes("thenewaceplace.com")) return "ace_cobrand_base";
    if (host.includes("promotionsnow.com")) return "pnow_cobrand_base";
    if (host.includes("foremostpromotions.com")) return "fmt_cobrand_base";
    if (host.includes("healthpromotionsnow.com")) return "hpn_cobrand_base";

    // Fallback (choose whatever makes sense for your default)
    return "pnow_cobrand_base";
}

/* -------------------------------------------
   Read objectID from the page
   (You said: document.querySelector('input[name="cbpid"]'))
-------------------------------------------- */
function getObjectIdFromPage() {
    const el = document.querySelector('input[name="cbpid"]');
    if (!el) return null;

    const v = (el.value || "").trim();
    return v ? v : null;
}

/* -------------------------------------------
   Attribution helpers (sessionStorage for current session + localStorage for cart persistence)
   Expected shape:
   {
     "index": "pnow_cobrand_base",
     "objectID": "992319",
     "queryID": "...",
     "position": 1
   }
-------------------------------------------- */
function getAlgoliaAttribution(productId) {
    try {
        const map = JSON.parse(sessionStorage.getItem("algolia_attrib_map") || "{}");
        return map[String(productId)] || null;
    } catch {
        return null;
    }
}

function clearAlgoliaAttribution(productId) {
    try {
        const map = JSON.parse(sessionStorage.getItem("algolia_attrib_map") || "{}");
        delete map[String(productId)];
        sessionStorage.setItem("algolia_attrib_map", JSON.stringify(map));
    } catch {}
}

/* -------------------------------------------
   Cart Attribution Storage (localStorage for persistence across days)
   Stores attribution per CART ITEM (not just product ID) to handle duplicates
   
   Structure:
   {
     "cartItem_timestamp_productId": {
       "productId": "992319",
       "index": "pnow_cobrand_base",
       "objectID": "992319",
       "queryID": "abc123",
       "position": 1,
       "timestamp": 1234567890
     }
   }
-------------------------------------------- */
function saveToCartAttribution(productId, attrib) {
    try {
        const stored = JSON.parse(localStorage.getItem("algolia_cart_attrib") || "{}");

        // Create unique cart item ID using timestamp + productId
        // This allows multiple instances of same product
        const timestamp = Date.now();
        const cartItemId = `item_${timestamp}_${productId}`;

        stored[cartItemId] = {
            productId: String(productId),
            index: attrib.index,
            objectID: attrib.objectID,
            queryID: attrib.queryID,
            position: attrib.position || null,
            timestamp: timestamp
        };

        localStorage.setItem("algolia_cart_attrib", JSON.stringify(stored));
        console.log(`[AIC] Saved cart attribution for item ${cartItemId}`);
    } catch (e) {
        console.warn("[AIC] Failed to save cart attribution:", e);
    }
}

/* -------------------------------------------
   Insights loader stub (IMPORTANT)

   If you only include the search-insights script without the stub,
   window.aa may be undefined depending on how it's loaded.
   This stub is the supported way: it creates window.aa and a .queue.
-------------------------------------------- */
(function ensureAaStubExists() {
    // If aa already exists as a function, don't touch it.
    if (typeof window.aa === "function") return;

    // Create the official stub with .queue
    window.AlgoliaAnalyticsObject = "aa";
    window.aa =
        window.aa ||
        function () {
            (window.aa.queue = window.aa.queue || []).push(arguments);
        };
})();

/* -------------------------------------------
   Insights init (hardcoded creds)
-------------------------------------------- */
function ensureInsightsInit() {
    // If Insights script hasn't executed yet, aa will still be our stub.
    // That's OK: init will be queued and executed once the real library loads.
    if (typeof window.aa !== "function") return false;

    // Prevent double-init
    if (window.__aa_inited) return true;

    try {
        window.aa("init", {
            appId: window.algoliaConfig.appId,
            apiKey: window.algoliaConfig.searchKey,
            useCookie: true,
        });

        window.__aa_inited = true;
        return true;
    } catch (e) {
        console.warn("[AIC] Algolia Insights init failed:", e);
        return false;
    }
}

/* -------------------------------------------
   Track product view (on Add-to-Cart page)

   Note: There is no viewedObjectIDsAfterSearch method.
   Product views use viewedObjectIDs (unattributed).
-------------------------------------------- */
function trackProductViewInternal() {
    if (!ensureInsightsInit()) return false;

    const pageObjectID = getObjectIdFromPage();
    if (!pageObjectID) return false;

    const index = resolveIndexFromHost();

    // Optional: prevent double-sending on the same page load
    // (remove this if you WANT multiple views on refreshes/back/forward)
    // const key = `aa_view_sent_${index}_${pageObjectID}`;
    // try {
    //     if (sessionStorage.getItem(key) === "1") return true;
    //     sessionStorage.setItem(key, "1");
    // } catch {}

    window.aa("viewedObjectIDs", {
        eventName: "Product Viewed",
        index: index,               // some docs show indexName; your implementation uses index
        objectIDs: [String(pageObjectID)],
        url: cleanUrl(window.location.href)
        
    });

    return true;
}


/* -------------------------------------------
   Track Add-to-Cart conversion

   - Uses convertedObjectIDsAfterSearch if we have queryID + matching objectID
   - Otherwise uses convertedObjectIDs (unattributed conversion)
   - SAVES attribution to localStorage for later purchase tracking
   
   NOTE: Attribution is saved here as a fallback, but the canonical save
   should happen on the cart page when "Proceed to Checkout" is clicked.
   This ensures we capture what's ACTUALLY in the cart at checkout time.
-------------------------------------------- */
function trackAddToCartConversionInternal() {
    // Make sure init is queued / done
    if (!ensureInsightsInit()) return false;

    const pageObjectID = getObjectIdFromPage();
    if (!pageObjectID) {
        console.warn("[AIC] No cbpid found on page; cannot send conversion.");
        return false;
    }

    const attrib = getAlgoliaAttribution(pageObjectID);

    // If session attribution exists but is for a different product, clear it.
    // This handles users landing directly on a product page or switching products.
    if (attrib?.objectID && String(attrib.objectID) !== String(pageObjectID)) {
        clearAlgoliaAttribution(pageObjectID);
    }

    const finalAttrib = getAlgoliaAttribution(pageObjectID); // re-read after potential clear
    const indexFromHost = resolveIndexFromHost();

    // Prefer attrib.index if present; otherwise compute from hostname
    const index = finalAttrib?.index || indexFromHost;

    // Save to localStorage for purchase tracking (fallback method)
    // The cart page should also call saveCartAttribution on "Proceed to Checkout"
    if (finalAttrib) {
        saveToCartAttribution(pageObjectID, {
            index: index,
            objectID: pageObjectID,
            queryID: finalAttrib.queryID || null,
            position: finalAttrib.position || null
        });
    } else {
        // No attribution from search, but still save product for unattributed purchase tracking
        saveToCartAttribution(pageObjectID, {
            index: index,
            objectID: pageObjectID,
            queryID: null,
            position: null
        });
    }

    // If we have a queryID, use "AfterSearch" conversion (best attribution)
    if (finalAttrib?.queryID) {
        window.aa("convertedObjectIDsAfterSearch", {
            eventName: "Add To Cart",
            index: index,
            queryID: finalAttrib.queryID,
            objectIDs: [String(pageObjectID)],
            url: cleanUrl(window.location.href)
        });
        return true;
    }

    // Otherwise send unattributed conversion
    window.aa("convertedObjectIDs", {
        eventName: "Add To Cart",
        index: index,
        objectIDs: [String(pageObjectID)],
        url: cleanUrl(window.location.href)
    });
    return true;
}

/* -------------------------------------------
   Public API for Alpine to call
-------------------------------------------- */
window.trackAddToCartConversion = function trackAddToCartConversion() {
    return trackAddToCartConversionInternal();
};

// Export saveToCartAttribution so cart page can use it
window.saveCartAttribution = saveToCartAttribution;


/* -------------------------------------------
   Optional: init ASAP (queues if library not ready yet)
   This is safe and helps ensure init happens before user clicks.
-------------------------------------------- */
(function boot() {
    // If DOM is still loading, wait; otherwise run now.
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", function () {
            ensureInsightsInit();
            trackProductViewInternal();
        });
    } else {
        ensureInsightsInit();
        trackProductViewInternal();
    }
})();